/**
 * The write side of `+ Motion` once its dialog has closed: create the Motion if asked, claim the
 * opportunity into it, and say what happened above the block the row is in.
 *
 * Same shape as `useLighthouseEdits`, deliberately: the dialog closes on Confirm and holds no
 * result, the outcome is an `Alert` (not a Flashbar — see there for the scroll-to-top a
 * Flashbar's dismiss caused), and a failure is reported, not retried.
 *
 * A claim is ONE `ClaimCandidates` call with one ACCELERATE claim under a fresh wave id — the
 * service UPSERTs the wave, and there is no batch across rows. The service reports a pair it did
 * not insert as a conflict with a reason (`MotionClaimService`); `already_in_motion` is the one a
 * user can meet from here, and it is not an error.
 *
 * No refetch: nothing on the dashboard shows which Motion an opportunity is in, so a claim changes
 * nothing it renders.
 */

import { useCallback, useState } from 'react';
import type { AccountPipelineOpportunity } from '@amzn/ivthub-client';
import {
  ClaimCandidatesCommand,
  CreateMotionCommand,
  getIvtClient,
} from '../../client/IvtHubClient';
import { denialHint } from '../../utils/authorization';
import { newWaveId } from '../../utils/waveId';
import type { AddMotionChoice } from './components/AddMotionModal';
import type { LighthouseNotice } from './useLighthouseEdits';

export type MotionBlock = 'lighthouse' | 'reclaim';

/** The row a claim is for, and which block it is in — the notice goes above that block. */
export interface MotionClaimTarget {
  block: MotionBlock;
  accountId: string | undefined;
  accountName: string | undefined;
  opp: AccountPipelineOpportunity;
}

export interface MotionClaimState {
  noticesFor: (block: MotionBlock) => LighthouseNotice[];
  claim: (target: MotionClaimTarget, choice: AddMotionChoice) => Promise<void>;
}

const NOTICE_ID = 'motion-claim';

const messageOf = (e: unknown) => denialHint(e) ?? ((e as Error | null)?.message || '请求失败');

export function useMotionClaim(): MotionClaimState {
  const [state, setState] = useState<{ block: MotionBlock; notice: LighthouseNotice } | null>(null);

  const show = useCallback(
    (block: MotionBlock, notice: Omit<LighthouseNotice, 'id' | 'dismissible' | 'onDismiss'>) =>
      setState({
        block,
        notice: {
          ...notice,
          id: NOTICE_ID,
          dismissible: !notice.loading,
          onDismiss: () => setState(null),
        },
      }),
    [],
  );

  const claim = useCallback(
    async ({ block, accountId, accountName, opp }: MotionClaimTarget, choice: AddMotionChoice) => {
      const oppLabel = opp.oppName ?? opp.opptyId ?? '';
      show(block, { type: 'info', loading: true, content: `正在把「${oppLabel}」加入 Motion…` });

      let motionId: string;
      let motionName: string;
      if (choice.kind === 'existing') {
        ({ motionId, motionName } = choice);
      } else {
        try {
          const out = await getIvtClient().send(new CreateMotionCommand(choice.values));
          const id = out.motion?.motionId;
          if (!id) throw new Error('新建的 Motion 没有返回 id');
          motionId = id;
          motionName = choice.values.name;
        } catch (e) {
          show(block, { type: 'error', content: `未能新建 Motion「${choice.values.name}」：${messageOf(e)}` });
          return;
        }
      }

      try {
        const out = await getIvtClient().send(
          new ClaimCandidatesCommand({
            motionId,
            waveId: newWaveId(),
            claims: [
              {
                // `accountName` is required on the wire, and the service writes it into
                // account_cache as a stub keyed by `accountId`. A flagged account can have NO
                // cache row, which this page exists to surface — so without a name the id is
                // withheld (it is optional on ACCELERATE) rather than planted under a blank.
                accountName: accountName ?? '',
                accountId: accountName ? accountId : undefined,
                opptyId: opp.opptyId,
                oppName: opp.oppName,
                arr: opp.arr,
                oppStage: opp.sfdcStage,
                poolType: 'ACCELERATE',
              },
            ],
          }),
        );
        const conflict = out.conflicts?.[0];
        if ((out.claimed ?? 0) > 0) {
          show(block, { type: 'success', content: `已将「${oppLabel}」加入 Motion「${motionName}」` });
        } else if (conflict?.reason === 'already_in_motion') {
          show(block, { type: 'info', content: `「${oppLabel}」已在 Motion「${motionName}」中` });
        } else {
          show(block, {
            type: 'error',
            content: `未能把「${oppLabel}」加入 Motion「${motionName}」：${conflict?.reason ?? '未知原因'}`,
          });
        }
      } catch (e) {
        // A Motion created a moment ago still exists — say so, or the user makes a second one.
        const prefix = choice.kind === 'new' ? `已新建 Motion「${motionName}」，但` : '';
        show(block, {
          type: 'error',
          content: `${prefix}未能把「${oppLabel}」加入 Motion「${motionName}」：${messageOf(e)}`,
        });
      }
    },
    [show],
  );

  const noticesFor = useCallback(
    (block: MotionBlock) => (state && state.block === block ? [state.notice] : []),
    [state],
  );

  return { noticesFor, claim };
}
